/**
 * *! This helper has unit tests.
 * If you make any changes to this file, you should run the firmware unit tests:
 * npm test src/utest/helpers/firmware_compare_helpers.test.ts
 */

export type FWVersion = {
	date: Date;
	time: Date | string;
	version: string;
	major: number;
	minor: number;
	patch: number;
	beta: boolean;
	info: string;
};

/**
 * Compare firmware version strings using the specified operator.
 *
 * @param {string} fwVersion - Full firmware version string.
 * @param {'<' | '=' | '>' | '<=' | '>='} operator - Comparison operator.
 * @param {string} compareVersion - Version to compare (e.g., '0.0.9').
 * @returns {boolean} `true` if the comparison satisfies the operator, `false` otherwise.
 *
 * @example
 * compareFWVersions('20240911-180733/v0.0.9@9d34a7c3', '=', '0.0.9'); // returns true
 * compareFWVersions('v0.0.9@9d34a7c3', '>', '0.0.8'); // returns true
 * compareFWVersions('20241106-105858/v1.14.1-rc1-6-gfb43488e8', '<=', '1.1.1'); // returns false
 */
export const compareFWVersions = (
	fwVersion: string,
	operator: '<' | '=' | '>' | '<=' | '>=',
	compareVersion: string,
): boolean => {
	const version = parseFWVersion(fwVersion);
	const compareVer = parseFWVersion(compareVersion);
	if (!version || !compareVer) return false;
	const compare = (a: number, b: number) => (a > b ? 1 : a < b ? -1 : 0);
	const result =
		compare(version.major, compareVer.major) ||
		compare(version.minor, compareVer.minor) ||
		compare(version.patch, compareVer.patch);
	return (
		(operator === '>' && result > 0) ||
		(operator === '<' && result < 0) ||
		(operator === '=' && result === 0) ||
		(operator === '>=' && result >= 0) ||
		(operator === '<=' && result <= 0)
	);
};

/**
 * Compare firmware version dates using the specified operator.
 *
 * @param {string} fwVersion - Full firmware version string.
 * @param {'<' | '==' | '>' | '<=' | '>='} operator - Comparison operator.
 * @param {string} compareDate - Date to compare (YYYYMMDD format).
 * @returns {boolean} `true` if the comparison satisfies the operator, `false` otherwise.
 *
 * @example
 * compareFWDates('20240911-180733/v0.0.9@9d34a7c3', '=', '20240911'); // returns true
 * compareFWDates('20240911-180733/v0.0.9@9d34a7c3', '<=', '20241011'); // returns true
 * compareFWDates('20240911-133734/1.4.99-blugwg3prod0-g9385873', '<=', '20240911'); // returns true
 */
export const compareFWDates = (
	fwVersion: string,
	operator: '<' | '=' | '>' | '<=' | '>=',
	compareDate: string,
): boolean => {
	const version = parseFWVersion(fwVersion);
	const match = (compareDate || '').match(/^(\d{4})(\d{2})(\d{2})$/);
	if (!version || !match) return false;
	const compareFWDate = new Date(`${match[1]}-${match[2]}-${match[3]}`)?.getTime();
	const versionDate = version.date?.getTime();
	return (
		(operator === '>' && versionDate > compareFWDate) ||
		(operator === '<' && versionDate < compareFWDate) ||
		(operator === '=' && versionDate === compareFWDate) ||
		(operator === '>=' && versionDate >= compareFWDate) ||
		(operator === '<=' && versionDate <= compareFWDate)
	);
};

/**
 * Parses a firmware version string into structured components.
 *
 * @param {string} fwVersion Full firmware version string.
 * @returns {FWVersion|null} Parsed version details or `null` if parsing fails.
 */
export const parseFWVersion = (fwVersion: string): FWVersion | null => {
	/**
	 * Regex for parsing firmware version strings.
	 *
	 * - ((\d{4})(\d{2})(\d{2})-? - Capture full date (YYYYMMDD) with optional hyphen
	 * - ((\d{2})(\d{2})(\d{2}))?\/? - Capture optional time (HHMMSS) and optional "/"
	 * - ([vr]?\.?(\d+)\.(\d+)[.-]?(\d+))? - Capture version number (optional 'v' or 'r', major.minor.patch)
	 * - [@-]?(.*) - Capture optional build metadata (e.g., git hash, release info)
	 */
	const match = (fwVersion || '').match(
		/^((\d{4})(\d{2})(\d{2})-?((\d{2})(\d{2})(\d{2}))?\/?)?([vr]?\.?(\d+)\.(\d+)[.-]?(\d+))?[@-]?(.*)$/,
	);
	const date = match[1];
	const version = match[9] || '';
	if (!match || (!version && !date)) return null;
	const dateStr = `${match[2]}-${match[3]}-${match[4]}`;
	const major = Number(match[10]) || 0;
	const minor = Number(match[11]) || 0;
	const patch = Number(match[12]) || 0;
	const info = match[13] || '';
	return {
		date: new Date(dateStr),
		time: match[5] ? new Date(`${dateStr}T${match[6]}:${match[7]}:${match[8]}`) : '',
		version: version ? `${major}.${minor}.${patch}` : '',
		beta: /^beta/.test(info),
		major,
		minor,
		patch,
		info,
	};
};

/**
 * Compare two firmware versions.
 *
 * @param {string} firstVersion - First firmware version string.
 * @param {string} secondVersion - Second firmware version string.
 * @returns {-1 | 0 | 1 | NaN} Comparison result: `-1` if firstVersion is newer, `1` if secondVersion is newer, `0` if they are equal.
 *
 * @example
 * compare2Versions('20240912-180733/v0.0.9@g60f1725', '20240911-180733/v0.0.9@9d34a7c3'); // -1
 * compare2Versions('20240117-143235/v1.14.1-rc1-3-gd248c7ff7', '20230911-094108/v1.14.0-rc2-1-gcb846234a'); // -1
 * compare2Versions('20241007-074728/1.4.99-blugwg3prod2-g689f175', '20250221-145347/1.5.1-beta1-g172c5bb'); // 1
 * compare2Versions('20231030-110713/proem50prod0-209-gfbb5dc930-wifi', '20240305-144205/proem50prod0-519-g06d53b014-log-http-v2-2-dirty'); // 1
 * compare2Versions('1.2.0', '1.1.9'); // -1
 * compare2Versions('20240911/1.2.0', 'v1.2.0-g12a9327-master'); // 0
 * compare2Versions('20240911/v0.0.9', '1.0.0@22b1e50-master'); // 1
 * compare2Versions('v0.0.9@g60f1725', '0.0.10');// 1
 * compare2Versions('v0.test', '0.0.10');// NaN
 */
/**
 * Check whether a device's firmware version matches a filter value.
 * The filter value is the raw firmware string stored in the dropdown option.
 * Comparison uses the full raw string for an exact match first, then falls back
 * to semver triple comparison for cross-format matching.
 *
 * @param {string} deviceFwString - Raw firmware string from the device.
 * @param {string} filterValue    - Value from the filter dropdown option.
 * @returns {boolean} `true` when the device firmware matches the filter.
 */
export const matchesFirmwareVersion = (deviceFwString: string, filterValue: string): boolean => {
	if (!deviceFwString || !filterValue) return false;
	if (deviceFwString === filterValue) return true;

	const deviceVer = parseFWVersion(deviceFwString);
	const filterVer = parseFWVersion(filterValue);
	if (!deviceVer || !filterVer) return false;

	const deviceDisplay = formatFirmwareDisplay(deviceFwString);
	const filterDisplay = formatFirmwareDisplay(filterValue);
	if (deviceDisplay === filterDisplay && deviceDisplay !== deviceFwString) return true;

	if (!deviceVer.version || !filterVer.version) return false;
	return (
		deviceVer.major === filterVer.major &&
		deviceVer.minor === filterVer.minor &&
		deviceVer.patch === filterVer.patch
	);
};

export type DeviceGenLabel = 'Gen1' | 'Gen2+' | 'BLU' | 'Wall Display';

/**
 * Determine the generation label for a device, used as a prefix in the firmware filter list.
 */
export const getDeviceGenLabel = (device: { gen?: string | number; type?: string }): DeviceGenLabel => {
	if (device.gen === 'GBLE') return 'BLU';
	if (/^SAWD/.test(device.type || '')) return 'Wall Display';
	if (Number(device.gen) >= 2) return 'Gen2+';
	return 'Gen1';
};

/**
 * Produce a human-readable display string from a raw firmware version string.
 * Handles semantic versions, rc/beta suffixes, and raw build-only strings.
 *
 * - `20250220-082359/1.5.0-g0087a06`  ->  `1.5.0`
 * - `20241106-105858/v1.14.1-rc1-6-gfb43488e8`  ->  `1.14.1-rc1`
 * - `20250310-083015/1.5.1-beta2-g322cd2a`  ->  `1.5.1-beta2`
 * - `20231030-110713/proem50prod0-209-gfbb5dc930-wifi`  ->  raw string (no semver)
 * - `v0.0.9@9d34a7c3`  ->  `0.0.9`
 * - `0.11.24@22b1e50-master`  ->  `0.11.24`
 */
export const formatFirmwareDisplay = (fwString: string): string => {
	if (!fwString) return '';
	const parsed = parseFWVersion(fwString);
	if (!parsed || !parsed.version) return fwString;

	const qualifier = extractVersionQualifier(parsed.info);
	return qualifier ? `${parsed.version}-${qualifier}` : parsed.version;
};

/**
 * Extract an rc/beta/test qualifier from the info/metadata portion of a parsed firmware version.
 * Returns the qualifier string (e.g. `rc1`, `beta2`) or empty string if none found.
 */
export const extractVersionQualifier = (info: string): string => {
	if (!info) return '';
	const match = info.match(/^(rc\d*|beta\d*|test\d*)/i);
	return match ? match[1] : '';
};

/**
 * Extract the raw firmware version string for a device, regardless of generation.
 *
 * @param device         - Device object.
 * @param devicesSettings - Settings store snapshot (keyed by real device id).
 * @param devicesStatus   - Status store snapshot (keyed by device id).
 * @returns The raw firmware version string, or an empty string when unavailable.
 */
export const getDeviceFirmwareString = (
	device: { id: string; gen?: string | number; fwID?: string; channel?: number },
	devicesSettings: Record<string, any>,
	devicesStatus: Record<string, any>,
): string => {
	const id = device.id?.indexOf('_') > -1 ? device.id.split('_')[0] : device.id;

	if (device.gen === 'GBLE') {
		if (typeof device.fwID !== 'undefined') return device.fwID;
		const versionInStatus = devicesStatus[device.id]?.[`fwversion:${device.channel}`]?.version;
		return versionInStatus ?? '';
	}

	if (Number(device.gen) >= 2) {
		return devicesSettings[id]?.sys?.device?.fw_id ?? '';
	}

	return devicesSettings[id]?.fw ?? '';
};

/**
 * Collect unique firmware versions from all devices, grouped by generation label.
 *
 * Each option title includes a generation prefix (e.g. "Gen2+ 1.5.0", "BLU 0.0.9")
 * and properly handles rc/beta qualifiers and non-semantic build strings.
 *
 * @returns Sorted array of `{ title, value }` objects suitable for a `Select` component.
 */
export const getUniqueFirmwareVersions = (
	devices: Record<string, any>,
	devicesSettings: Record<string, any>,
	devicesStatus: Record<string, any>,
): { title: string; value: string }[] => {
	const seen = new Map<string, { title: string; value: string }>();

	for (const id in devices) {
		if (!Object.prototype.hasOwnProperty.call(devices, id)) continue;
		const device = devices[id];
		const fwString = getDeviceFirmwareString(device, devicesSettings, devicesStatus);
		if (!fwString) continue;

		const display = formatFirmwareDisplay(fwString);
		const genLabel = getDeviceGenLabel(device);
		const key = `${genLabel}::${display}`;

		if (seen.has(key)) continue;
		seen.set(key, {
			title: `${genLabel}  ${display}`,
			value: fwString,
		});
	}

	const options = Array.from(seen.values());

	const genOrder: Record<string, number> = { 'Gen2+': 0, Gen1: 1, BLU: 2, 'Wall Display': 3 };
	options.sort((a, b) => {
		const genA = a.title.split('  ')[0];
		const genB = b.title.split('  ')[0];
		const genDiff = (genOrder[genA] ?? 99) - (genOrder[genB] ?? 99);
		if (genDiff !== 0) return genDiff;

		const va = parseFWVersion(a.value);
		const vb = parseFWVersion(b.value);
		if (!va || !vb) return 0;
		return vb.major - va.major || vb.minor - va.minor || vb.patch - va.patch;
	});

	return options;
};

export const compare2Versions = (
	firstVersion: string,
	secondVersion: string,
): -1 | 0 | 1 | typeof NaN => {
	const firstVer = parseFWVersion(firstVersion);
	const secondVer = parseFWVersion(secondVersion);
	if (!firstVer || !secondVer) return NaN;
	const firstDate = firstVer.date.getTime();
	const secondDate = secondVer.date.getTime();
	if (!isNaN(firstDate) && !isNaN(secondDate) && firstDate !== secondDate) {
		return firstDate > secondDate ? -1 : 1;
	}
	const compare = (a: number, b: number): -1 | 0 | 1 => (a > b ? -1 : a < b ? 1 : 0);
	return (
		compare(firstVer.major, secondVer.major) ||
		compare(firstVer.minor, secondVer.minor) ||
		compare(firstVer.patch, secondVer.patch)
	);
};
